import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { PROTOCOL_DISCLAIMER_SHORT, type PublicShare } from "@handvero/shared";
import { api, errorText } from "../api";
import "./unterlagen.css";

/*
 * Seite für Kunden einer Firma: Über den Link aus der Mail lädt der Kunde die Protokolle herunter, ohne Konto.
 * Im Vordergrund steht die Firma mit Logo; HandVero steht nur klein darunter.
 */

function day(iso: string) {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function Unterlagen() {
  const { token = "" } = useParams();
  const [share, setShare] = useState<PublicShare | null>(null);
  const [error, setError] = useState("");
  const base = `/api/public/shares/${encodeURIComponent(token)}`;

  useEffect(() => {
    document.title = "Protokolle zum Herunterladen";
    // Persönliche Links gehören nicht in Suchmaschinen.
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    api
      .get<PublicShare>(base)
      .then((s) => {
        setShare(s);
        document.title = `${s.company}: Protokolle`;
      })
      .catch((err) => setError(errorText(err)));
    return () => robots.remove();
  }, [base]);

  return (
    <div className="ul-page">
      <main className="ul-card">
        {error ? (
          <>
            <h1>Link nicht verfügbar</h1>
            <p className="ul-error" role="alert">
              {error}
            </p>
          </>
        ) : !share ? (
          <div className="ul-loading" role="status" aria-label="Wird geladen" />
        ) : (
          <>
            <header className="ul-head">
              {share.hasLogo && <img src={`${base}/logo`} alt={share.company} className="ul-logo" />}
              <div>
                <b>{share.company}</b>
                {share.line && <small>{share.line}</small>}
              </div>
            </header>
            <h1>{share.protocols.length === 1 ? "Ihr Protokoll" : "Ihre Protokolle"}</h1>
            {share.message && (
              <blockquote className="ul-msg">
                <p>{share.message}</p>
                {share.sender && <cite>{share.sender}</cite>}
              </blockquote>
            )}
            <ul className="ul-list">
              {share.protocols.map((p) => (
                <li key={p.id}>
                  <span className="ul-ico" aria-hidden="true">
                    PDF
                  </span>
                  <span className="ul-text">
                    <b>{p.title}</b>
                    <small>
                      {p.templateName}
                      {p.completedAt && ` · ${day(p.completedAt)}`}
                    </small>
                  </span>
                  {p.available ? (
                    <span className="ul-btns">
                      <a className="ul-btn ghost" href={`${base}/protocols/${p.id}?ansehen=1`} target="_blank" rel="noreferrer">
                        Ansehen
                      </a>
                      <a className="ul-btn" href={`${base}/protocols/${p.id}`} download>
                        Herunterladen
                      </a>
                    </span>
                  ) : (
                    <small className="ul-gone">Nicht mehr verfügbar</small>
                  )}
                </li>
              ))}
            </ul>
            <p className="ul-note">
              Dieser Link ist bis zum {day(share.expiresAt)} gültig. Speichern Sie die PDFs, wenn Sie sie länger brauchen. Fragen zu den Protokollen beantwortet Ihnen{" "}
              {share.company} direkt.
            </p>
            <p className="ul-note small">{PROTOCOL_DISCLAIMER_SHORT}</p>
          </>
        )}
      </main>
      <footer className="ul-foot">
        Bereitgestellt mit{" "}
        <a href="/" className="ul-brand">
          <span>Hand</span>Vero
        </a>{" "}
        · <a href="/rechtliches">Impressum und Datenschutz</a>
      </footer>
    </div>
  );
}
